import { getInitData } from "./telegram";

export type Status = "playing" | "won" | "lost";

export interface MeStats {
  name: string;
  streak: number;
  maxStreak: number;
  games: number;
  wins: number;
  avgScore: number | null;
  bestScore: number | null;
}

export interface GroupEntry {
  userId: number;
  name: string;
  status: Status;
  score: number | null;
  streak: number;
}

export interface GameState<V> {
  game: string;
  date: string;
  number: number;
  status: Status;
  view: V;
  me: MeStats;
  group: GroupEntry[];
  extra?: unknown;
}

export class ApiFailure extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly extra?: unknown,
  ) {
    super(code);
  }
}

async function call<T>(game: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/games/${game}${path}`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-telegram-init-data": getInitData() },
      body: JSON.stringify(body ?? {}),
    });
  } catch {
    throw new ApiFailure("network", 0);
  }
  if (!res.ok) {
    const err = (await res.json().catch(() => ({ error: "server_error" }))) as { error?: string; extra?: unknown };
    throw new ApiFailure(err.error ?? "server_error", res.status, err.extra);
  }
  return (await res.json()) as T;
}

export interface OverviewGame {
  number: number;
  status: Status | null;
  score: number | null;
  played: number;
}
export interface Overview {
  date: string;
  /** Games that are on for this day — switched-off ones are absent. */
  games: Record<string, OverviewGame>;
  /** Switched-off ids (Арена included) the hub hides. */
  off: string[];
  /** The requester may flip switches (⚙️). */
  admin: boolean;
}

/** on = in the list; off = switched off; back = still off today, returns on `back`. */
export interface SwitchInfo {
  state: "on" | "off" | "back";
  since: string | null;
  back: string | null;
}
export interface Switches {
  date: string;
  games: Record<string, SwitchInfo>;
}

export const api = {
  /** `date` = a past archive day: your status for that day (archive plays included). */
  overview: (date?: string) => call<Overview>("hub", "/overview", date ? { date } : {}),
  /** `date` = archive replay of a past Kyiv day (no streaks, no chips post). */
  state: <V>(game: string, date?: string) => call<GameState<V>>(game, "/state", date ? { date } : {}),
  guess: <V>(game: string, input: string, date?: string) =>
    call<GameState<V>>(game, "/guess", date ? { input, date } : { input }),
  giveUp: <V>(game: string, date?: string) => call<GameState<V>>(game, "/giveup", date ? { date } : {}),
  names: (game: string) => call<{ names: { uk: string; en: string }[] }>(game, "/names"),
  /** Арена: live-room op (join/start/answer/state/again). */
  arena: <T>(op: string, extra: Record<string, unknown> = {}) => call<T>("arena", "/room", { op, ...extra }),
  /** Admin: every game's switch. */
  switches: () => call<Switches>("admin", "/switches"),
  /** Admin: off hides the game now, on brings it back from tomorrow. */
  setSwitch: (game: string, on: boolean) => call<Switches>("admin", "/switch", { game, on }),
  /** Малюнок: publish your canvas and unlock the gallery (PNG data URL). */
  drawing: <V>(png: string, date?: string) =>
    call<GameState<V>>("maliunok", "/drawing", date ? { png, date } : { png }),
  async shape(date?: string): Promise<string | null> {
    try {
      const url = date ? `/api/games/worldle/shape?date=${date}` : "/api/games/worldle/shape";
      const res = await fetch(url, { headers: { "x-telegram-init-data": getInitData() } });
      if (!res.ok) return null;
      return URL.createObjectURL(await res.blob());
    } catch {
      return null;
    }
  },
};

export const ERROR_COPY: Record<string, string> = {
  unauthorized: "Відкрий гру через Telegram — кнопка в чаті або t.me/ch1ps_bot/games",
  not_configured: "Сервер ще не налаштований. Скажи чіпсу.",
  network: "Нема зв'язку. Спробуй ще раз.",
  upstream: "Джерело не відповідає. Спробуй за хвилину.",
  server_error: "Щось зламалось. Спробуй ще раз.",
  unknown_game: "Такої гри нема.",
  game_disabled: "Цю гру поки вимкнули. Інші - в хабі 🎮",
  forbidden: "Це тільки для адміна.",
};
